<template>
	<v-col cols="10" class="flex-none mx-auto">
		<div class="inventory-colors">
			<div v-for="(option, i) in sortedColorOptions" :key="i" class="inventory-colors-options">
				<v-btn
					v-if="canManageInventory"
					class="inventory-colors-options-action"
					text
					@click="removeColorOption(option)"
				>
					Remove Option
				</v-btn>

				<div class="inventory-colors-options-order">
					<v-btn :disabled="!canManageInventory" icon @click="changePosition('up', option, i)"
						><v-icon>mdi-triangle</v-icon></v-btn
					>
					<v-btn :disabled="!canManageInventory" icon @click="changePosition('down', option, i)"
						><v-icon>mdi-triangle</v-icon></v-btn
					>
				</div>

				<div class="inventory-colors-form">
					<div class="form-input" v-if="option?.type?.toLowerCase() === 'color'">
						<label>Color Palette</label>
						<v-combobox
							v-model="selectedColorPalette[i]"
							:items="colorPalettes"
							item-text="name"
							item-value="colorPaletteId"
							dense
							outlined
							hide-details="auto"
							@input.capture="searchColorPalettes"
							:disabled="!canManageInventory"
						></v-combobox>
						<v-btn
							depressed
							outlined
							@click="renderColorData(i)"
							class="ml-2"
							:disabled="!selectedColorPalette || !canManageInventory"
						>
							Add
						</v-btn>
					</div>

					<div
						v-for="palette in option.colorPalettes"
						:key="palette.colorPaletteId"
						class="form-input"
					>
						<v-btn
							depressed
							class="inventory-colors-form-palettes"
							@click="removeColorPalette(i, palette)"
							:disabled="!canManageInventory"
						>
							{{ palette.name }}
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</div>

					<div class="form-input">
						<label>{{
							option.type?.toLowerCase() === "color" ? "Option Code" : "Option Name"
						}}</label>
						<v-text-field
							:disabled="!canManageInventory"
							v-model="option.name"
							outlined
							dense
							hide-details="auto"
						></v-text-field>
					</div>

					<div class="form-input">
						<label>Option Description</label>
						<v-text-field
							v-model="option.description"
							outlined
							dense
							hide-details="auto"
							:disabled="!canManageInventory"
						></v-text-field>
					</div>
				</div>

				<div class="inventory-colors-table" v-if="option?.type?.toLowerCase() === 'color'">
					<div
						v-for="(palette, paletteIndex) in option.colorPalettes"
						:key="paletteIndex"
						class="inventory-colors-table-container"
					>
						<div class="flex-table" role="table-head">
							<div class="flex-table-row" role="row-group">
								<div class="flex-table-cell inventory-colors-table-info" role="column-header">
									<h4>{{ palette.name }}</h4>

									<div class="form-input">
										<label>Up-charge amount</label>
										<currency-text-field
											v-model="palette.upcharge"
											:disabled="!canManageInventory"
										/>
									</div>
								</div>
							</div>
						</div>
						<div class="flex-table" role="table-body">
							<div class="flex-table-row" role="row-group">
								<div class="flex-table-cell" role="cell">
									<ul class="inventory-colors-table-list">
										<li
											v-for="(color, colorIndex) in palette.colors.filter(
												(c) => c.isRetired === false
											)"
											:key="colorIndex"
											class="inventory-colors-table-list-color"
											:class="color.isVisible == false ? 'color-disabled' : null"
										>
											<ColorDisplay :hex-code="color.hexCode" :label="color.label" :is-custom-color="color.isCustomColor" shape="circle" size="large">
												<v-btn
													v-show="color.isVisible == null || color.isVisible == true"
													icon
													@click="
														color.isVisible = false;
														$forceUpdate();
													"
													:disabled="!canManageInventory"
												>
													<v-icon style="font-size: 2rem">mdi-eye</v-icon>
												</v-btn>
												<v-btn
													v-show="color.isVisible == false"
													icon
													@click="
														color.isVisible = true;
														$forceUpdate();
													"
													:disabled="!canManageInventory"
												>
													<v-icon style="font-size: 2rem">mdi-eye-off</v-icon>
												</v-btn>
											</ColorDisplay>
											<p class="inventory-colors-table-list-color-tooltip">
												{{ color.label }} - {{ color.description }}
											</p>
										</li>
									</ul>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<template>
				<div class="text-start">
					<v-menu offset-y>
						<template v-slot:activator="{ on, attrs }">
							<v-btn
								v-if="canManageInventory"
								class="inventory-colors-action"
								text
								v-bind="attrs"
								v-on="on"
							>
								<v-icon>mdi-plus</v-icon> Add Option
							</v-btn>
						</template>
						<v-list>
							<v-list-item key="color" @click="addOption('color')">
								<v-list-item-title style="font-size: 1.5rem"
									><v-icon>mdi-palette</v-icon> Color Option</v-list-item-title
								>
							</v-list-item>
							<v-list-item key="freeText" @click="addOption('freetext')">
								<v-list-item-title style="font-size: 1.5rem"
									><v-icon>mdi-form-textbox</v-icon> Free Text Option</v-list-item-title
								>
							</v-list-item>
						</v-list>
					</v-menu>
				</div>
			</template>
		</div>
	</v-col>
</template>

<script>
import gql from "graphql-tag";
import ColorDisplay from "@/components/ColorDisplay.vue";

export default {
	name: "InventoryItemColorView",
	props: {
		canManageInventory: {
			type: Boolean,
			default: false,
		},
	},
	components: {
		ColorDisplay,
	},
	data() {
		return {
			itemData: {
				colorOptions: [
					{
						type: "color",
						name: "",
						description: "",
						position: 1,
						colorPalettes: [],
					},
				],
			},
			activeOptionIndex: null,
			selectedColorPalette: [],
			colorPalettes: [],
			searchTerm: "",
		};
	},
	apollo: {
		colorPalettes: {
			query() {
				return gql`
					query GetColorPalettes($searchTerm: String) {
						colorPalettes(
							take: 500
							order: { name: ASC }
							where: { isTemplate: { eq: true } }
							searchTerm: $searchTerm
						) {
							items {
								colorPaletteId
								name
								description
								colors {
									colorId
									description
									hexCode
									label
									isVisible
									isRetired
								}
							}
						}
					}
				`;
			},
			fetchPolicy: "no-cache",
			variables() {
				return {
					searchTerm: this.searchTerm || "",
				};
			},
			update: (data) => data.colorPalettes.items,
		},
	},
	methods: {
		searchColorPalettes(item) {
			if (item && item.target && item.target.value) {
				this.searchTerm = item.target.value;
			} else {
				this.searchTerm = "";
			}
		},
		renderColorData(index) {
			let paletteByIdx = this.selectedColorPalette[index];

			//check if the paletteById exists in our options colors and add it if it doesnt
			let matchingPaletteIndex = this.itemData.colorOptions[index].colorPalettes.findIndex(
				(obj) => obj.colorPaletteId == this.selectedColorPalette[index].colorPaletteId
			);
			if (matchingPaletteIndex == -1) {
				//remove reactivity so we can save the new pallete in data without changing it globally
				let newPalette = JSON.parse(JSON.stringify(paletteByIdx));
				newPalette.templateColorPaletteId = newPalette.colorPaletteId;
				this.itemData.colorOptions[index].colorPalettes.push(newPalette);
				//clear the selected palette
				this.selectedColorPalette[index] = null;
			}
		},

		removeColorPalette(index, palette) {
			//remove the passed in palette for the specific option
			this.itemData.colorOptions[index].colorPalettes = this.itemData.colorOptions[
				index
			].colorPalettes.filter((obj) => obj.colorPaletteId !== palette.colorPaletteId);
		},

		addOption(type) {
			const newOption = {
				type: type,
				name: "",
				description: "",
				position: this.itemData.colorOptions.length + 1,
				colorPalettes: [],
			};

			this.itemData.colorOptions.push(newOption);
			this.normalizePositions();
		},

		removeColorOption(option) {
			this.itemData.colorOptions = this.itemData.colorOptions.filter((opt) => opt !== option);
			this.normalizePositions();
		},

		// Ensure all positions are correct after adding a new option
		normalizePositions() {
			this.itemData.colorOptions.forEach((option, index) => {
				option.position = index + 1;
			});
			this.$forceUpdate();
		},

		changePosition(direction, option, index) {
			let sortedOptions = this.sortedColorOptions;
			let currentOption = sortedOptions[index];
			let targetIndex = direction === "up" ? index - 1 : index + 1;

			if (targetIndex >= 0 && targetIndex < sortedOptions.length) {
				// Find actual indices in the original array
				let actualCurrentIndex = this.itemData.colorOptions.findIndex(
					(opt) => opt.position === currentOption.position
				);
				let actualTargetIndex = this.itemData.colorOptions.findIndex(
					(opt) => opt.position === sortedOptions[targetIndex].position
				);

				// Swap the 'position' property values in the original array
				let temp = this.itemData.colorOptions[actualCurrentIndex].position;
				this.itemData.colorOptions[actualCurrentIndex].position =
					this.itemData.colorOptions[actualTargetIndex].position;
				this.itemData.colorOptions[actualTargetIndex].position = temp;

				// Sort and reactively update the original array
				let newArray = this.itemData.colorOptions.slice().sort((a, b) => a.position - b.position);
				this.$set(this.itemData, "colorOptions", newArray);
			}
		},
	},
	computed: {
		storeItem() {
			return this.$store.getters.inventoryItem;
		},
		sortedColorOptions() {
			const optionsCopy = this.itemData.colorOptions.slice();
			return optionsCopy.sort((a, b) => {
				if (a.position !== b.position) {
					return a.position - b.position;
				}
				return a.colorOptionId - b.colorOptionId;
			});
		},
	},
	watch: {
		// Re-adopt the store object whenever the parent view's async fetch
		// replaces it — children mount before the parent's mounted() completes,
		// so a one-shot read here can capture a stale tree and orphan edits.
		storeItem: {
			immediate: true,
			handler(item) {
				this.itemData = item;
			},
		},
	},
	beforeDestroy() {
		//push current item data to state
		this.$store.dispatch("updateInventoryItem", this.itemData);

		//let parent component know data has been updated in state
		this.$emit("updated");
	},
};
</script>

<style lang="scss" scoped>
.inventory {
	&-colors {
		&-form {
			position: relative;
			display: flex;
			flex-flow: column;
			height: 100%;
			width: 60%;
			margin: 0 auto;

			& .form-input:first-child {
				flex-flow: row wrap;
			}

			& > .form-radio,
			& > .form-input {
				margin: initial;
				margin-bottom: 1.25rem;
			}

			.v-select {
				width: auto;
			}

			.v-btn {
				background-color: $color-white;
				border-color: $color-gray;
			}

			&-palettes.v-btn {
				display: flex;
				flex-grow: 1;
				justify-content: space-between;
				background-color: $color-gray-light-2;
				border-color: initial;
				color: $color-gray;
			}
		}

		&-table {
			display: flex;
			flex-flow: column nowrap;
			height: 100%;
			margin-top: 2rem;

			&-container {
				flex-grow: 1;
				background-color: $color-white;
				margin-bottom: 1.5rem;

				&:last-child {
					margin-bottom: 0;
				}
			}

			.flex-table-cell {
				&:nth-child(1) {
					width: 100%;
					flex-flow: row wrap;
				}
			}

			&-info {
				align-items: center;
				width: 100%;

				h4 {
					font-size: 1.6rem;
					font-weight: 700;
					margin-right: 2.5rem;
				}

				.form-input {
					width: 40rem;
					margin: 0;
					flex-flow: row;
					align-items: center;
					justify-content: flex-start;

					label {
						width: initial;
						margin-right: 1rem;
					}
				}
			}

			&-list {
				display: flex;
				flex-flow: row wrap;
				align-items: center;
				width: 100%;
				padding: 1rem 0 0.8rem;

				&-color {
					position: relative;
					display: flex;
					flex-flow: column nowrap;
					justify-content: center;
					align-items: center;
					margin: 1rem 1.5rem;

					&:hover .inventory-colors-table-list-color-tooltip {
						visibility: visible;
						opacity: 1;
					}

					span {
						position: relative;
						display: block;
						width: 4rem;
						height: 4rem;
						border: 1px solid $color-gray-light-2;
						border-radius: 25px;

						.v-btn {
							position: absolute;
							top: -1.2rem;
							right: -1.8rem;
							width: initial;
							height: initial;
						}
					}

					&.color-disabled {
						opacity: 0.4;
					}

					&-tooltip {
						visibility: hidden;
						opacity: 0;
						position: absolute;
						top: 130%;
						border-radius: 5px;
						padding: 0.5rem 0.8rem;
						width: 15rem;
						background-color: $color-gray-dark;
						font-size: 1.2rem;
						color: $color-white;
						text-align: center;
						z-index: 1;
						transition: all 0.4s ease;

						&::after {
							content: "";
							position: absolute;
							bottom: 100%;
							left: 50%;
							margin-left: -5px;
							border-width: 5px;
							border-style: solid;
							border-color: transparent transparent $color-gray-dark transparent;
						}
					}
				}
			}
		}

		& &-options {
			position: relative;
			padding: 3rem 0;
			border-top: 1px solid $color-gray-dark;

			&:first-of-type {
				margin-top: 2rem;
			}

			&:last-of-type {
				border-bottom: 1px solid $color-gray-dark;
				margin-bottom: 1rem;
			}

			&-action {
				position: absolute;
				right: 0;
			}

			&-order {
				position: absolute;
				right: 0;
				top: 10rem;
				display: flex;
				flex-flow: column;

				.v-btn {
					.v-icon {
						font-size: 3rem;
					}

					&:last-child {
						transform: rotate(180deg);
					}
				}
			}
		}

		&-action {
			padding: 0 1.6rem 0 1.2rem !important;
		}
	}
}
</style>
